import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseconf';
import { useInstitution } from '../../context/InstitutionContext';

export default function SubjectManagement() {
  const { currentInstitution } = useInstitution();

  const [subjects, setSubjects] = useState([]);
  const [branches, setBranches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    credits: 3,
    branch_or_class: '',
    semester_or_level: '',
    primary_teacher_id: '',
    total_lectures: 30,
    total_practicals: 0,
    internal_marks: 25,
    external_marks: 75
  });

  // Fetch subjects, branches, and classes
  useEffect(() => {
    if (!currentInstitution?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch branches
        const branchCollection = collection(db, 'institutions', currentInstitution.id, 'branches');
        const branchSnap = await getDocs(branchCollection);
        setBranches(branchSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        // Fetch classes
        const classCollection = collection(db, 'institutions', currentInstitution.id, 'classes');
        const classSnap = await getDocs(classCollection);
        setClasses(classSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        // Fetch subjects
        const subjectCollection = collection(db, 'institutions', currentInstitution.id, 'subjects');
        const subjectSnap = await getDocs(subjectCollection);
        setSubjects(subjectSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load subjects');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentInstitution?.id]);

  const handleOpenDialog = (subject = null) => {
    if (subject) {
      setEditingId(subject.id);
      setFormData({
        name: subject.name,
        code: subject.code,
        description: subject.description || '',
        credits: subject.credits || 3,
        branch_or_class: subject.branch_or_class || '',
        semester_or_level: subject.semester_or_level || '',
        primary_teacher_id: subject.primary_teacher_id || '',
        total_lectures: subject.total_lectures || 30,
        total_practicals: subject.total_practicals || 0,
        internal_marks: subject.internal_marks || 25,
        external_marks: subject.external_marks || 75
      });
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        credits: 3,
        branch_or_class: '',
        semester_or_level: '',
        primary_teacher_id: '',
        total_lectures: 30,
        total_practicals: 0,
        internal_marks: 25,
        external_marks: 75
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: ['credits', 'total_lectures', 'total_practicals', 'internal_marks', 'external_marks'].includes(name)
        ? parseInt(value)
        : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (!formData.name.trim()) {
        setError('Subject name is required');
        return;
      }
      if (!formData.code.trim()) {
        setError('Subject code is required');
        return;
      }
      if (!currentInstitution?.id) {
        setError('Institution not found');
        return;
      }

      const subjectCollection = collection(db, 'institutions', currentInstitution.id, 'subjects');

      if (editingId) {
        // Update existing subject
        const subjectRef = doc(db, 'institutions', currentInstitution.id, 'subjects', editingId);
        await updateDoc(subjectRef, {
          ...formData,
          updatedAt: serverTimestamp()
        });

        setSubjects(prev =>
          prev.map(s => s.id === editingId ? { id: editingId, ...formData } : s)
        );
      } else {
        // Add new subject
        const docRef = await addDoc(subjectCollection, {
          ...formData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: 'active'
        });

        setSubjects(prev => [...prev, { id: docRef.id, ...formData }]);
      }

      handleCloseDialog();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Failed to save subject');
    }
  };

  const handleDelete = async (subjectId) => {
    if (window.confirm('Are you sure you want to delete this subject?')) {
      try {
        await deleteDoc(doc(db, 'institutions', currentInstitution.id, 'subjects', subjectId));
        setSubjects(prev => prev.filter(s => s.id !== subjectId));
      } catch (err) {
        console.error('Error deleting subject:', err);
        setError('Failed to delete subject');
      }
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  const selectedOptions = currentInstitution?.type === 'college'
    ? branches.map(b => ({ id: b.id, name: `${b.name} (${b.code})` }))
    : classes.map(c => ({ id: c.id, name: `${c.name}` }));

  const semesterOptions = currentInstitution?.type === 'college'
    ? Array.from({ length: 8 }, (_, i) => ({ id: i + 1, name: `Semester ${i + 1}` }))
    : Array.from({ length: 12 }, (_, i) => ({ id: i + 1, name: `Class ${i + 1}` }));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Subject Management
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage subjects for {currentInstitution?.name}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Subject
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Subjects Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Subject Name</strong></TableCell>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>{currentInstitution?.type === 'college' ? 'Branch' : 'Class'}</strong></TableCell>
              <TableCell align="center"><strong>Credits/Hours</strong></TableCell>
              <TableCell align="center"><strong>Internal/External</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subjects.length > 0 ? (
              subjects.map((subject) => (
                <TableRow key={subject.id} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {subject.name}
                    </Typography>
                    {subject.description && (
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {subject.description.substring(0, 50)}...
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{subject.code}</TableCell>
                  <TableCell>
                    {selectedOptions.find(opt => opt.id === subject.branch_or_class)?.name || '-'}
                  </TableCell>
                  <TableCell align="center">
                    {subject.credits || subject.total_lectures}
                  </TableCell>
                  <TableCell align="center">
                    {subject.internal_marks}/{subject.external_marks}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenDialog(subject)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        color="error"
                        onClick={() => handleDelete(subject.id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    No subjects found. Click "Add Subject" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Subject' : 'Add New Subject'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subject Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Data Structures"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subject Code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="e.g., CS101"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={currentInstitution?.type === 'college' ? 'Branch' : 'Class'}
                name="branch_or_class"
                value={formData.branch_or_class}
                onChange={handleChange}
              >
                {selectedOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label={currentInstitution?.type === 'college' ? 'Semester' : 'Class/Level'}
                name="semester_or_level"
                value={formData.semester_or_level}
                onChange={handleChange}
              >
                {semesterOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="text"
                label={currentInstitution?.type === 'college' ? 'Credits' : 'Total Hours'}
                name="credits"
                value={formData.credits}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="text"
                label="Total Lectures"
                name="total_lectures"
                value={formData.total_lectures}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="text"
                label="Internal Marks"
                name="internal_marks"
                value={formData.internal_marks}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="text"
                label="External Marks"
                name="external_marks"
                value={formData.external_marks}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? 'Update' : 'Create'} Subject
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
